
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus'
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft'
import { Button, Flex } from '@mantine/core'
import { useEffect, useState, useContext } from 'react'

import { useAnalytics } from '@flecto/analytics'
import {
  useMutationCreateCustomer,
  useQueryGetAllCustomers,
} from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type CoreCustomer, type CoreCustomerPayload } from '@flecto/types'
import { Modal, useNotifications } from '@flecto/ui'
import { Icon } from '@flecto/ui'
import useMediaQuery from '@flecto/ui/lib/utils/useMediaQuery'
import { ModalsContext } from '@flecto/ui/src/components/Modal/ModalsProvider'

import {
  FormCustomer,
  FiltersCustomers,
  TableCustomers,
  usePagination,
} from '../../../customers'

export type ModalCustomerSelectProps = {
  setCustomer: (field: string, value: CoreCustomer) => void
}

export function ModalCustomerSelect({ setCustomer }: ModalCustomerSelectProps) {
  const track = useAnalytics('segment')
  const { isMobile } = useMediaQuery()
  const { t: tActions } = useTranslation('shared', { keyPrefix: 'actions' })
  const { t } = useTranslation('saas', { keyPrefix: 'insurances' })
  const { closeModal } = useContext(ModalsContext)
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false)
  const { showNotification } = useNotifications()
  const [customerSelected, setCustomerSelected] = useState<CoreCustomer | null>(
    null
  )

  const { isLoading: isCustomerLoading } = useMutationCreateCustomer()

  const {
    currentPage,
    totalRecords,
    searchTerm,
    filteredCustomers,
    pageSize,
    handlePageChange,
    setTotalRecords,
    handleSearch,
    setFilteredCustomers,
  } = usePagination(10)

  const {
    data: customers,
    isLoading,
    isFetched,
  } = useQueryGetAllCustomers({
    q: searchTerm,
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
    order: 'desc',
  })

  const handleSaveCustomer = async (createdCustomer?: CoreCustomer) => {
    try {
      setCustomer(
        'customer',
        customerSelected || (createdCustomer as CoreCustomer)
      )
    } catch {
      showNotification({
        type: 'error',
        message: t(
          'create_insurance.select_customer.notification_customer_selection_error'
        ),
      })
    } finally {
      showNotification({
        type: 'success',
        message: t(
          'create_insurance.select_customer.notification_customer_selection_success'
        ),
      })
    }
  }

  const handleSelect = (createdCustomer: CoreCustomerPayload) => {
    if (createdCustomer) {
      handleSaveCustomer(createdCustomer as CoreCustomer)
      closeModal()
    }
  }

  useEffect(() => {
    if (isFetched) {
      setTotalRecords(customers?.meta?.total_count ?? 0)
      setFilteredCustomers(customers?.data ?? [])
    }
  }, [customers, isFetched, setTotalRecords, setFilteredCustomers])

  return (
    <Modal size='xxl'>
      <Modal.Close />
      <Modal.Title>
        {t('create_insurance.select_customer.label_select_customer')}
      </Modal.Title>
      <Modal.Content>
        {isCreatingCustomer ? (
          <FormCustomer
            id='customer-form'
            onSuccess={handleSelect}
            hideSections={[
              'address',
              'discount',
              'identification',
              'language',
              'notes',
            ]}
          />
        ) : (
          <>
            <Flex align='center' justify='flex-end'>
              <FiltersCustomers onFilter={handleSearch} />
            </Flex>
            <TableCustomers
              mt='md'
              customers={filteredCustomers}
              isLoading={isLoading}
              hideColumns={[1]}
              withBorder={false}
              highlightOnHover
              onSelectSingleRecordChange={setCustomerSelected}
              withPaginationControlled={{
                currentPage,
                totalRecords,
                onPageChange: handlePageChange,
              }}
            />
          </>
        )}
      </Modal.Content>
      <Modal.FooterExtra>
        <Button
          variant='white'
          px={0}
          leftSection={
            <Icon icon={isCreatingCustomer ? faChevronLeft : faPlus} />
          }
          onClick={() => {
            setIsCreatingCustomer(!isCreatingCustomer)
            setCustomerSelected(null)
          }}
        >
          {isCreatingCustomer
            ? isMobile
              ? ''
              : t('create_insurance.select_customer.back_to_customers')
            : t('create_insurance.select_customer.add-customer')}
        </Button>
      </Modal.FooterExtra>
      <Modal.Cancel />
      <Modal.Submit
        loading={isCreatingCustomer ? isCustomerLoading : false}
        disabled={!isCreatingCustomer && !customerSelected}
        onClick={
          isCreatingCustomer
            ? undefined
            : () => {
                handleSaveCustomer()
                track('INSURANCE_FORM__CUSTOMER_SET', {
                  customer_id: customerSelected?.id ?? '',
                })
              }
        }
        form={isCreatingCustomer ? 'customer-form' : undefined}
        preventClose={isCreatingCustomer}
        type='submit'
      >
        {isCreatingCustomer
          ? isMobile
            ? t('create_insurance.select_customer.save_add_mobile')
            : t('create_insurance.select_customer.save_add')
          : tActions('save')}
      </Modal.Submit>
    </Modal>
  )
}
